import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getStatusChipSx, ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../constants/statusColors';
import {
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Login as LoginIcon,
  Update as UpdateIcon,
  Assignment as AssignmentIcon,
  CorporateFare as ParentCompanyIcon,
  Domain as SubsidiaryIcon,
  DeleteOutline as DeleteIcon,
  MarkEmailReadOutlined as ResendEmailIcon,
} from '@mui/icons-material';
import { csAdminApi } from '../../services/api';
import { mapCompanySecretaryFromApi, normalizeSecretaryAssignedCompanies } from '../../utils/apiMappers';
import ManageSecretaryCompaniesModal from '../../components/company-secretaries/ManageSecretaryCompaniesModal';
import AccountCreatedDialog from '../../components/users/AccountCreatedDialog';
import {
  shouldShowAccountSetupDialog,
  accountResendWelcomeMessage,
} from '../../utils/accountSetupDisplay';

const buildSecretaryStatistics = (assignedCompanies = []) => {
  const companies = Array.isArray(assignedCompanies) ? assignedCompanies : [];
  return {
    totalCompanies: companies.length,
    totalDirectors: 0,
    totalCommittees: 0,
    documentsUploaded: 0,
    lastMonthActivity: 0,
  };
};

const CompanySecretaryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [secretary, setSecretary] = useState(null);
  const [manageCompaniesOpen, setManageCompaniesOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [accountSetup, setAccountSetup] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  useEffect(() => {
    fetchSecretaryDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSecretaryDetails = async () => {
    try {
      setLoading(true);

      const response = await csAdminApi.getCompanySecretaryById(id);
        const data = mapCompanySecretaryFromApi(response.data?.data);
        if (!data) {
          setError('Company secretary not found');
          return;
        }
        const assignedCompanies = normalizeSecretaryAssignedCompanies(data);
        setSecretary({
          ...data,
          phone: data.phone || '—',
          createdDate: data.createdDate || data.created_at || null,
          lastLogin: data.lastLogin || data.last_login || null,
          assignedCompanies,
          recentActivity: data.recentActivity || [],
          statistics: data.statistics || buildSecretaryStatistics(assignedCompanies),
        });
        setError('');
    } catch (err) {
      console.error('Error fetching secretary details:', err);
      setError('Failed to load company secretary details');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const getActivityIcon = () => {
    return <UpdateIcon />;
  };

  const handleResendWelcomeEmail = async () => {

    try {
      setResendingEmail(true);
      const response = await csAdminApi.resendCompanySecretaryWelcomeEmail(id, {
        sendWelcomeEmail: true,
      });
      const setup = response.data?.data?.accountSetup;
      if (setup && shouldShowAccountSetupDialog(setup)) {
        setAccountSetup(setup);
        setAccountDialogOpen(true);
      } else {
        enqueueSnackbar(accountResendWelcomeMessage(setup), { variant: 'success' });
      }
    } catch (err) {
      console.error('Resend welcome email:', err);
      enqueueSnackbar(
        err.response?.data?.error?.message || 'Failed to resend welcome email',
        { variant: 'error' }
      );
    } finally {
      setResendingEmail(false);
    }
  };

  const handleDeleteSecretary = async () => {

    try {
      setDeleting(true);
      await csAdminApi.deleteCompanySecretary(id);
      enqueueSnackbar('Company secretary removed', { variant: 'success' });
      setDeleteDialogOpen(false);
      navigate('/company-secretaries');
    } catch (err) {
      console.error('Delete company secretary:', err);
      enqueueSnackbar(
        err.response?.data?.error?.message || 'Failed to remove company secretary',
        { variant: 'error' }
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !secretary) {
    return (
      <Box sx={{ px: 3, py: 3 }}>
        <Alert severity="error">{error || 'Company Secretary not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/company-secretaries')}
          sx={{ mt: 2 }}
        >
          Back to Company Secretaries
        </Button>
      </Box>
    );
  }

  const initials = secretary.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const stats = secretary.statistics || buildSecretaryStatistics(secretary.assignedCompanies);
  const canRemove = secretary.assignedCompanies.length === 0;

  return (
    <Box sx={{ px: 3, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/company-secretaries')}
          sx={{ mb: 2 }}
        >
          Back to Company Secretaries
        </Button>

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: '#D32F2F',
                  fontSize: '1.75rem',
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {secretary.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={secretary.status}
                    sx={{
                      ...getStatusChipSx(secretary.status),
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      borderRadius: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                    size="small"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="textSecondary">
                      {secretary.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="textSecondary">
                      {secretary.phone}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ResendEmailIcon />}
                onClick={handleResendWelcomeEmail}
                disabled={resendingEmail}
              >
                {resendingEmail ? 'Sending…' : 'Resend Welcome Email'}
              </Button>
              <Tooltip
                title={
                  canRemove
                    ? 'Permanently remove this account'
                    : 'Remove all company assignments before deleting this account'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={!canRemove}
                  >
                    Remove
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Edit Details">
                <IconButton
                  color="primary"
                  onClick={() => navigate(`/company-secretaries/${id}/edit`)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Companies
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196F3' }}>
                {stats.totalCompanies}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Directors
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#9C27B0' }}>
                {stats.totalDirectors}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Committees
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: ACTIVE_GREEN }}>
                {stats.totalCommittees}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Documents
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#FF9800' }}>
                {stats.documentsUploaded}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Activity (30d)
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#00BCD4' }}>
                {stats.lastMonthActivity}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              <ListItem sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#E3F2FD' }}>
                    <CalendarIcon sx={{ color: '#2196F3' }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Created Date"
                  secondary={
                    secretary.createdDate
                      ? new Date(secretary.createdDate).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                      : '—'
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: ACTIVE_GREEN_BG }}>
                    <LoginIcon sx={{ color: ACTIVE_GREEN }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Last Login"
                  secondary={
                    secretary.lastLogin
                      ? new Date(secretary.lastLogin).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Never'
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#FFF3E0' }}>
                    <BusinessIcon sx={{ color: '#FF9800' }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Assigned Companies"
                  secondary={`${secretary.assignedCompanies.length} companies`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Assigned Companies */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Assigned Companies
              </Typography>
              <Button
                size="small"
                startIcon={<AssignmentIcon />}
                variant="outlined"
                onClick={() => setManageCompaniesOpen(true)}
              >
                Manage
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <List>
              {secretary.assignedCompanies.map((company, index) => (
                <ListItem
                  key={company.id}
                  sx={{
                    px: 0,
                    borderBottom: index < secretary.assignedCompanies.length - 1 ? '1px solid #E0E0E0' : 'none',
                  }}
                >
                  <ListItemAvatar>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: company.type === 'Parent' ? '#E3F2FD' : '#F3E5F5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {company.type === 'Parent' ? (
                        <ParentCompanyIcon sx={{ fontSize: 24, color: '#1976D2' }} />
                      ) : (
                        <SubsidiaryIcon sx={{ fontSize: 24, color: '#7B1FA2' }} />
                      )}
                    </Box>
                  </ListItemAvatar>
                  <ListItemText
                    primary={company.name}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <Chip
                          label={company.type}
                          size="small"
                          sx={{
                            bgcolor: 'transparent',
                            color: '#D32F2F',
                            border: '1px solid #D32F2F',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px',
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {company.assignedDate
                            ? `Since ${new Date(company.assignedDate).toLocaleDateString('en-GB')}`
                            : 'Assignment date not available'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {secretary.assignedCompanies.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No companies assigned
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {secretary.recentActivity.map((activity, index) => (
                <ListItem
                  key={activity.id}
                  sx={{
                    px: 0,
                    borderBottom: index < secretary.recentActivity.length - 1 ? '1px solid #E0E0E0' : 'none',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#E3F2FD' }}>
                      {getActivityIcon()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={activity.action}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <Chip
                          label={activity.company}
                          size="small"
                          sx={{
                            bgcolor: '#F5F5F5',
                            color: '#616161',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px',
                            border: 'none',
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          •
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatTimestamp(activity.timestamp)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {secretary.recentActivity.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No recent activity
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <ManageSecretaryCompaniesModal
        open={manageCompaniesOpen}
        onClose={() => setManageCompaniesOpen(false)}
        secretary={secretary}
        onSaved={(updatedSecretary) => {
          setSecretary(updatedSecretary);
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Company Secretary?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the account for <strong>{secretary.name}</strong> (
            {secretary.email}). This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteSecretary} disabled={deleting}>
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <AccountCreatedDialog
        open={accountDialogOpen}
        onClose={() => {
          setAccountDialogOpen(false);
          setAccountSetup(null);
        }}
        title="Welcome Email — Credentials"
        subtitle="A new temporary password was generated. Share these credentials if email delivery failed."
        accountSetup={accountSetup}
      />
    </Box>
  );
};

export default CompanySecretaryDetails;
